import { useEffect, useState } from "react";
import { Star, FileText, Truck, Store } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const WS_STATUS = { sent: "#E67E22", seen: "#2B79C2", preparing: "#8E44AD", ready: "#1F9D55", collected: "#0B2A5B", rejected: "#C7353B", cancelled: "#7F8C8D" };

export function Stars({ value = 0, onChange, size = "w-4 h-4", testid = "stars" }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} className={onChange ? "cursor-pointer" : "cursor-default"} data-testid={`${testid}-${n}`}>
          <Star className={`${size} ${n <= Math.round(value) ? "fill-[#F39C12] text-[#F39C12]" : "text-[#CCCCCC]"}`} />
        </button>
      ))}
    </span>
  );
}

export function StatusBadge({ status, t }) {
  return <span className="px-2 py-0.5 rounded text-white text-xs font-bold" style={{ background: WS_STATUS[status] }} data-testid="ws-status">{t(`ws_${status}`)}</span>;
}

export function OrderLines({ o, money, t }) {
  return (
    <div className="text-xs space-y-0.5" data-testid="ws-lines">
      {o.lines.map((l, i) => <div key={i} className="flex justify-between gap-2"><span className="truncate">{l.qty} × {l.name}</span><b dir="ltr">{money(l.qty * l.price)}</b></div>)}
      {o.discount > 0 && <div className="flex justify-between text-[#1F9D55]"><span>{t ? t("discount") : "Discount"}{o.disc?.kind === "percent" ? ` ${o.disc.value}%` : ""}</span><b dir="ltr">-{money(o.discount)}</b></div>}
      <div className="flex justify-between border-t border-border pt-1 font-bold"><span>{t ? t("total") : "Total"}</span><span dir="ltr" data-testid="ws-order-total">{money(o.total)}</span></div>
    </div>
  );
}

export function DebtPanel({ debts, me, money, t, onOpen, onStatement }) {
  const list = me === "supplier" ? debts?.owed_to_us : debts?.we_owe;
  if (!list?.length) return null;
  const total = list.reduce((s, x) => s + (x.balance || 0), 0);
  return (
    <div className="rounded-xl border border-[#F2C9CB] bg-[#FDF5F5] p-3 space-y-1.5" data-testid="ws-debt-panel">
      <div className="flex items-center justify-between"><span className="font-bold text-sm text-[#C7353B]">{t(me === "supplier" ? "ws_debt_owed_to_us" : "ws_debt_we_owe")}</span><span className="font-black" dir="ltr" data-testid="ws-debt-total">{money(total)}</span></div>
      {list.map((x) => <div key={x.id} className="flex items-center justify-between text-sm" data-testid={`ws-debt-${x.id}`}><span className="truncate">{x.name}</span><span className="flex items-center gap-2"><b dir="ltr">{money(x.balance)}</b>{onStatement && <button type="button" className="text-xs underline text-[#0B2A5B] inline-flex items-center gap-0.5" onClick={() => onStatement(x)} data-testid={`ws-stmt-${x.id}`}><FileText className="w-3 h-3" />{t("ws_statement")}</button>}{onOpen && <button type="button" className="text-xs underline text-[#0B2A5B]" onClick={() => onOpen(x)} data-testid={`ws-debt-open-${x.id}`}>{t(me === "supplier" ? "ws_debt_collect" : "ws_debt_pay")}</button>}</span></div>)}
      <p className="text-[11px] text-muted-foreground">{t(me === "supplier" ? "ws_debt_hint_sup" : "ws_debt_hint_buyer")}</p>
    </div>
  );
}

const logoCache = new Map();
export function PartnerLogo({ shopId, logoId, name, type, size = "w-10 h-10", testid }) {
  const [url, setUrl] = useState(logoCache.get(shopId) || null);
  useEffect(() => {
    if (!shopId || !logoId || logoCache.has(shopId)) return;
    api.get(`/wholesale/logo/${shopId}`, { responseType: "blob" }).then((r) => { const u = URL.createObjectURL(r.data); logoCache.set(shopId, u); setUrl(u); }).catch(() => logoCache.set(shopId, null));
  }, [shopId, logoId]);
  const Icon = type === "wholesale" ? Truck : Store;
  return (
    <span className={`${size} rounded-xl bg-white border border-border grid place-items-center overflow-hidden shrink-0`} title={name} data-testid={testid}>
      {url ? <img src={url} alt={name || ""} className="w-full h-full object-contain" /> : <span className="w-full h-full grid place-items-center bg-[#0B2A5B] text-white">{name ? <b className="text-sm">{name.trim().slice(0, 1).toUpperCase()}</b> : <Icon className="w-5 h-5" />}</span>}
    </span>
  );
}

export function PartyCard({ label, shopId, logoId, name, type, phone, address, t }) {
  return (
    <div className="rounded border border-[#dbe6ef] p-2 flex items-center gap-2">
      <PartnerLogo shopId={shopId} logoId={logoId} name={name} type={type} size="w-12 h-12" />
      <div className="min-w-0"><div className="text-[#5b6b75] uppercase">{label}</div><b className="block truncate">{name}</b><div className="text-[#5b6b75]">{type ? t(`type_${type}`) : ""}{phone ? <span dir="ltr"> · {phone}</span> : ""}</div>{address && <div className="text-[#5b6b75] truncate">{address}</div>}</div>
    </div>
  );
}

export const wsErr = (e, t) => { const m = errMsg(e); if (String(m).startsWith("insufficient_stock:")) { const [, name, av] = m.split(":"); return `${t("ws_insufficient")}: ${name} (${t("ws_available")} ${av})`; } return m; };
